/**
 * EmployeeSelector Component
 * Search employees by name, phone, employee no/CFMS ID with UDA & Position dropdowns.
 * Respects current jurisdictions.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { ListSkeleton } from '@/components/base/Skeleton';
import { createPortal } from 'react-dom';
import { mastersApi } from '../services/wmsService';

export interface SelectedEmployee {
  employee_id: number;
  employee_code: string;
  employee_name: string;
  phone?: string;
  email?: string;
  designation_id?: number;
  designation_name?: string;
  tenantid?: string;
  jurisdiction_ids?: number[];
}

interface EmployeeSelectorProps {
  selectedEmployee: SelectedEmployee | null;
  onSelect: (employee: SelectedEmployee) => void;
  onClear: () => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  /** Filter to only show employees with jurisdiction matching these IDs */
  jurisdictionIds?: number[];
}

export default function EmployeeSelector({
  selectedEmployee,
  onSelect,
  onClear,
  label = 'Select Employee',
  placeholder = 'Search by name, phone, employee no...',
  disabled = false,
  jurisdictionIds,
}: EmployeeSelectorProps) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<SelectedEmployee[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });

  // Filter states
  const [tenants, setTenants] = useState<{ tenantid: string; emp_count: number }[]>([]);
  const [designations, setDesignations] = useState<{ id: number; name: string }[]>([]);
  const [departments, setDepartments] = useState<{ dprtmnt_id: number; dprtmnt_nm: string }[]>([]);
  const [selectedTenant, setSelectedTenant] = useState('');
  const [selectedDesignation, setSelectedDesignation] = useState<number | ''>('');
  const [selectedDepartment, setSelectedDepartment] = useState<number | ''>('');
  const [filtersLoaded, setFiltersLoaded] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const LIMIT = 15;

  // Load filter options
  const loadFilters = useCallback(async () => {
    if (filtersLoaded) return;
    try {
      const [t, d, dept] = await Promise.all([
        mastersApi.getEmployeeTenants(),
        mastersApi.getDesignations(true),
        mastersApi.getDepartmentsList(),
      ]);
      setTenants(t || []);
      setDesignations((d || []).map((x: any) => ({ id: x.id, name: x.name })));
      setDepartments((dept || []).map((x: any) => ({ dprtmnt_id: x.dprtmnt_id || x.id, dprtmnt_nm: x.dprtmnt_nm || x.name })));
      setFiltersLoaded(true);
    } catch (e) {
      console.error('Failed to load employee filters:', e);
    }
  }, [filtersLoaded]);

  // Search employees
  const doSearch = useCallback(async (searchText: string, pg: number, tenant: string, desigId: number | '', deptId: number | '') => {
    setLoading(true);
    try {
      const params: any = { page: pg, limit: LIMIT };
      if (searchText.trim()) params.search = searchText.trim();
      if (tenant) params.tenantid = tenant;
      if (desigId) params.designation_id = desigId;
      if (deptId) params.department_id = deptId;

      const res = await mastersApi.searchEmployees(params);
      let data: SelectedEmployee[] = res.data || [];

      // Client-side jurisdiction filter if jurisdictionIds provided
      if (jurisdictionIds?.length) {
        data = data.filter(emp =>
          emp.jurisdiction_ids?.some((jid: number) => jurisdictionIds.includes(jid))
        );
      }

      setResults(data);
      setTotal(res.total || 0);
    } catch (e) {
      console.error('Employee search error:', e);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [jurisdictionIds]);

  // Debounced search on text change
  useEffect(() => {
    if (!dropdownOpen) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      doSearch(search, 1, selectedTenant, selectedDesignation, selectedDepartment);
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [search, selectedTenant, selectedDesignation, selectedDepartment, dropdownOpen]);

  // Pagination
  useEffect(() => {
    if (dropdownOpen && page > 1) {
      doSearch(search, page, selectedTenant, selectedDesignation, selectedDepartment);
    }
  }, [page]);

  // Position dropdown
  const updateDropdownPos = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
  }, []);

  const openDropdown = () => {
    if (disabled) return;
    loadFilters();
    updateDropdownPos();
    setDropdownOpen(true);
    doSearch(search, 1, selectedTenant, selectedDesignation, selectedDepartment);
  };

  // Close on outside click
  useEffect(() => {
    if (!dropdownOpen) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (containerRef.current?.contains(target)) return;
      if (target.closest('.emp-selector-portal')) return;
      setDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [dropdownOpen]);

  // Reposition on scroll/resize
  useEffect(() => {
    if (!dropdownOpen) return;
    const handler = () => updateDropdownPos();
    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);
    return () => { window.removeEventListener('scroll', handler, true); window.removeEventListener('resize', handler); };
  }, [dropdownOpen, updateDropdownPos]);

  const handleSelect = (emp: SelectedEmployee) => {
    onSelect(emp);
    setDropdownOpen(false);
    setSearch('');
  };

  const totalPages = Math.ceil(total / LIMIT);

  // Format tenant id for display (ap.vijayawada -> Vijayawada)
  const fmtTenant = (t: string) => {
    const parts = t.split('.');
    const name = parts[parts.length - 1] || t;
    return name.charAt(0).toUpperCase() + name.slice(1);
  };

  return (
    <div ref={containerRef}>
      {label && <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>}

      {selectedEmployee ? (
        <div className="flex items-center justify-between px-3 py-2 bg-indigo-50 border border-indigo-200 rounded-lg">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
              <i className="ri-user-line text-indigo-600 text-sm"></i>
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{selectedEmployee.employee_name}</p>
              <p className="text-xs text-gray-500 truncate">
                {selectedEmployee.employee_code}
                {selectedEmployee.designation_name && ` · ${selectedEmployee.designation_name}`}
                {selectedEmployee.phone && ` · ${selectedEmployee.phone}`}
              </p>
            </div>
          </div>
          {!disabled && (
            <button onClick={onClear} className="ml-2 text-gray-400 hover:text-red-500 flex-shrink-0">
              <i className="ri-close-circle-fill"></i>
            </button>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={openDropdown}
          disabled={disabled}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-left text-sm text-gray-400 hover:border-indigo-400 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed flex items-center gap-2"
        >
          <i className="ri-user-search-line"></i>
          {placeholder}
        </button>
      )}

      {dropdownOpen && createPortal(
        <div
          className="emp-selector-portal fixed bg-white border border-gray-200 rounded-xl shadow-2xl z-[9999] flex flex-col"
          style={{ top: dropdownPos.top, left: dropdownPos.left, width: Math.max(dropdownPos.width, 480), maxHeight: 460 }}
        >
          {/* Search + Filters */}
          <div className="p-3 border-b space-y-2 flex-shrink-0">
            {/* Search input */}
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Name, phone, employee no / CFMS ID..."
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                autoFocus
              />
            </div>
            {/* Filter row */}
            <div className="flex gap-2">
              <select
                value={selectedTenant}
                onChange={e => { setSelectedTenant(e.target.value); setPage(1); }}
                className="flex-1 px-2 py-1.5 border border-gray-300 rounded-lg text-xs focus:ring-1 focus:ring-indigo-500 outline-none"
              >
                <option value="">All UDAs</option>
                {tenants.map(t => (
                  <option key={t.tenantid} value={t.tenantid}>{fmtTenant(t.tenantid)} ({t.emp_count})</option>
                ))}
              </select>
              <select
                value={selectedDesignation}
                onChange={e => { setSelectedDesignation(e.target.value ? Number(e.target.value) : ''); setPage(1); }}
                className="flex-1 px-2 py-1.5 border border-gray-300 rounded-lg text-xs focus:ring-1 focus:ring-indigo-500 outline-none"
              >
                <option value="">All Positions</option>
                {designations.map(d => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
              <select
                value={selectedDepartment}
                onChange={e => { setSelectedDepartment(e.target.value ? Number(e.target.value) : ''); setPage(1); }}
                className="flex-1 px-2 py-1.5 border border-gray-300 rounded-lg text-xs focus:ring-1 focus:ring-indigo-500 outline-none"
              >
                <option value="">All Departments</option>
                {departments.map(d => (
                  <option key={d.dprtmnt_id} value={d.dprtmnt_id}>{d.dprtmnt_nm}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Results */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="p-4"><ListSkeleton rows={4} /></div>
            ) : results.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <i className="ri-user-search-line text-2xl"></i>
                <p className="text-sm mt-1">No employees found</p>
              </div>
            ) : (
              results.map(emp => (
                <button
                  key={emp.employee_id}
                  onClick={() => handleSelect(emp)}
                  className="w-full px-3 py-2.5 text-left hover:bg-indigo-50 border-b border-gray-100 last:border-0 flex items-center gap-3 transition-colors"
                >
                  <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-semibold text-gray-600">
                      {emp.employee_name?.charAt(0)?.toUpperCase() || '?'}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900 truncate">{emp.employee_name}</span>
                      <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded flex-shrink-0">{emp.employee_code}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                      {emp.designation_name && <span>{emp.designation_name}</span>}
                      {emp.phone && <span>· {emp.phone}</span>}
                      {emp.tenantid && <span>· {fmtTenant(emp.tenantid)}</span>}
                    </div>
                  </div>
                  {emp.jurisdiction_ids?.length ? (
                    <span className="text-[9px] text-green-600 bg-green-50 px-1.5 py-0.5 rounded flex-shrink-0">
                      {emp.jurisdiction_ids.length} jurisdiction{emp.jurisdiction_ids.length > 1 ? 's' : ''}
                    </span>
                  ) : null}
                </button>
              ))
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-3 py-2 border-t bg-gray-50 text-xs text-gray-500 flex-shrink-0">
              <span>{total} employee{total !== 1 ? 's' : ''} found</span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="px-2 py-1 rounded hover:bg-gray-200 disabled:opacity-30"
                >
                  <i className="ri-arrow-left-s-line"></i>
                </button>
                <span>{page} / {totalPages}</span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="px-2 py-1 rounded hover:bg-gray-200 disabled:opacity-30"
                >
                  <i className="ri-arrow-right-s-line"></i>
                </button>
              </div>
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}
